import { Box, ToggleButton, ToggleButtonGroup } from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { default as ReactApexChart } from "react-apexcharts";
import { useSelector } from "react-redux";
import CustomSelect from "../../../../components/Select/Select";

const chartStyle = {
  background: "#fff",
  borderRadius: "16px",
  padding: "20px",
  boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
  flex: 1,
};

const chartContainer = {
  display: "flex",
  gap: "20px",
  marginTop: "20px",
};

const ageCategories = ["Booked Slots", "Available Slots"];

export default function UtilizationCapacity() {
  const { utilizationVsCapacity } = useSelector(
    (state) => state.doctorDashboard
  );
  const [selectedVets, setSelectedVets] = useState([]);
  const [selectedViewType, setSelectedViewType] = useState("dayView");

  useEffect(() => {
    setSelectedVets(
      utilizationVsCapacity?.slice(0, 5)?.map((item) => {
        return { name: item?.doctor, value: item?.doctor, id: item?.doctorId };
      })
    );
  }, [utilizationVsCapacity]);

  const getVetlist = () => {
    return utilizationVsCapacity?.map((item) => {
      return { name: item?.doctor, value: item?.doctor, id: item?.doctorId };
    });
  };

  // Chart Series - Booked Slots and Available Slots for each vet
  const ageSeries = useMemo(() => {
    if (!utilizationVsCapacity || !selectedVets?.length) return [];

    // Get booked slots data for each vet
    const bookedSlotsData = selectedVets?.map((vet, i) => {
      const vetData = utilizationVsCapacity?.find(
        (item) => item?.doctorId === vet?.id
      );
      return vetData?.[selectedViewType]?.bookedSlots ?? 0;
    });

    // Get available slots data for each vet
    const availableSlotsData = selectedVets?.map((vet, i) => {
      const vetData = utilizationVsCapacity?.find(
        (item) => item?.doctorId === vet?.id
      );
      return vetData?.[selectedViewType]?.availableSlots ?? 0;
    });

    return [
      {
        name: ageCategories[0], // "Booked Slots"
        data: bookedSlotsData,
      },
      {
        name: ageCategories[1], // "Available Slots"
        data: availableSlotsData,
      },
    ];
  }, [utilizationVsCapacity, selectedVets]);

  const ageOptions = useMemo(
    () => ({
      chart: { type: "bar", stacked: true, toolbar: { show: false } },
      plotOptions: {
        bar: { horizontal: false, columnWidth: "40%", borderRadius: 10 },
      },
      dataLabels: {
        enabled: true,
        style: { fontSize: "13px", fontWeight: "bold", colors: ["#fff"] },
      },
      colors: ["#1E90FF", "#FF69B4"],
      xaxis: { categories: selectedVets?.map((vet) => vet?.name) },
      legend: { position: "top", horizontalAlign: "left" },
      tooltip: {
        custom: function ({ series, seriesIndex, dataPointIndex, w }) {
          const vetId = selectedVets[dataPointIndex]?.id || "";
          // Get vet data for additional information
          const vetData = utilizationVsCapacity?.find(
            (item) => item?.doctorId === vetId
          );
          const bookedSlots = vetData?.dayView?.bookedSlots ?? 0;
          const overtime =
            vetData?.dayView?.overtimeMinutes ?? vetData?.overtime ?? 0;
          const availableSlots = vetData?.dayView?.availableSlots ?? 0;

          let tooltipContent = "";

          if (seriesIndex === 0) {
            // Booked Slots - show booked slots and overtime
            tooltipContent =
              '<div class="apexcharts-tooltip-title" style="font-family: Helvetica, Arial, sans-serif; font-size: 12px; background-color: #EBEBEB; color: #000; padding: 8px; margin: 0;">' +
              selectedVets[dataPointIndex]?.name +
              '<span class="apexcharts-tooltip-marker" style="background-color: ' +
              w.globals.colors[seriesIndex] +
              '; margin-left: 5px;"></span>' +
              "</div>" +
              '<div class="apexcharts-tooltip-series-group" style="order: 1; display: flex; background-color: #fff; padding: 8px;">' +
              '<div class="apexcharts-tooltip-text" style="font-family: Helvetica, Arial, sans-serif; font-size: 12px; color: #000;">' +
              '<div class="apexcharts-tooltip-y-group">' +
              '<span class="apexcharts-tooltip-text-y-label">Booked Slots: </span>' +
              '<span class="apexcharts-tooltip-text-y-value">' +
              bookedSlots +
              "</span>" +
              "</div>" +
              '<div class="apexcharts-tooltip-y-group">' +
              '<span class="apexcharts-tooltip-text-y-label">Overtime: </span>' +
              '<span class="apexcharts-tooltip-text-y-value">' +
              overtime +
              "</span>" +
              "</div>" +
              "</div>" +
              "</div>";
          } else {
            // Available Slots - show only available slots
            tooltipContent =
              '<div class="apexcharts-tooltip-title" style="font-family: Helvetica, Arial, sans-serif; font-size: 12px; background-color: #EBEBEB; color: #000; padding: 8px; margin: 0;">' +
              selectedVets[dataPointIndex]?.name +
              '<span class="apexcharts-tooltip-marker" style="background-color: ' +
              w.globals.colors[seriesIndex] +
              '; margin-left: 5px;"></span>' +
              "</div>" +
              '<div class="apexcharts-tooltip-series-group" style="order: 1; display: flex; background-color: #fff; padding: 8px;">' +
              '<div class="apexcharts-tooltip-text" style="font-family: Helvetica, Arial, sans-serif; font-size: 12px; color: #000;">' +
              '<div class="apexcharts-tooltip-y-group">' +
              '<span class="apexcharts-tooltip-text-y-label">Available Slots: </span>' +
              '<span class="apexcharts-tooltip-text-y-value">' +
              availableSlots +
              "</span>" +
              "</div>" +
              "</div>" +
              "</div>";
          }

          return tooltipContent;
        },
      },
    }),
    [utilizationVsCapacity, selectedVets]
  );

  if (!utilizationVsCapacity || !selectedVets?.length) return null;

  return (
    <Box>
      <div className="flex-row">
        <div className="flex1-end">
          <div className="w20Per">
            <CustomSelect
              list={getVetlist()}
              value={selectedVets ?? []}
              name="selectedVets"
              handleChange={(e) => {
                if (
                  e?.target?.value?.length > 5 ||
                  e?.target?.value?.length === 0
                ) {
                  return;
                }
                setSelectedVets(e?.target?.value);
              }}
              label=""
              multiSelectSearchCheck
              backgroundColor="#fff"
            />
          </div>
        </div>
        <Box display="flex" justifyContent="flex-end" className="ml20">
          <ToggleButtonGroup
            value={selectedViewType}
            exclusive
            onChange={(_, val) => val && setSelectedViewType(val)}
            size="small"
            sx={{
              backgroundColor: "#f0f2f5",
              borderRadius: "12px",

              // Styles for all grouped toggle buttons
              "& .MuiToggleButtonGroup-grouped": {
                borderRadius: "7px",
                borderColor: "transparent",
                color: "#333",
                transition: "none", // remove hover effect if needed

                // Only apply to selected button
                "&.Mui-selected": {
                  backgroundColor: "#005EB8",
                  color: "#fff",
                },
              },
            }}
          >
            <ToggleButton value="dayView">Day</ToggleButton>
            <ToggleButton value="weekView">Week</ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </div>
      <div style={chartContainer}>
        <div style={chartStyle}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <h3 style={{ margin: 0 }}>Utilization vs Capacity</h3>
          </div>
          <hr />
          <ReactApexChart
            options={ageOptions}
            series={ageSeries}
            type="bar"
            height={300}
          />
        </div>
      </div>
    </Box>
  );
}
